import { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Trash2 } from "lucide-react";

function LeadDetailsModal({ lead, campaignTitle, onClose }) {
  if (!lead) return null;
  
  return (
    <div data-testid="lead-details-overlay" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div data-testid="lead-details-modal" role="dialog" aria-modal="true" aria-labelledby="lead-details-title" className="bg-white w-full max-w-2xl max-h-[85dvh] overflow-y-auto break-words rounded shadow-lg p-6 space-y-4" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between border-b pb-3">
          <div id="lead-details-title" data-testid="lead-details-title" className="font-semibold text-xl">Lead Details</div>
          <Button data-testid="lead-details-close" variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 font-medium">Name</div>
              <div data-testid="lead-detail-name" className="mt-1 break-all">{lead.user_name || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 font-medium">Email</div>
              <div data-testid="lead-detail-email" className="mt-1 break-all">{lead.user_email}</div>
            </div>
          </div>
          
          {(lead.user_phone || lead.user_address || lead.postcode) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {lead.user_phone && <div><div className="text-sm text-gray-600 font-medium">Phone</div><div data-testid="lead-detail-phone" className="mt-1 break-all">{lead.user_phone}</div></div>}
            {lead.user_address && <div><div className="text-sm text-gray-600 font-medium">Address</div><div data-testid="lead-detail-address" className="mt-1 break-all">{lead.user_address}</div></div>}
            {lead.postcode && <div><div className="text-sm text-gray-600 font-medium">Postcode</div><div data-testid="lead-detail-postcode" className="mt-1">{lead.postcode}</div></div>}
          </div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 font-medium">Campaign</div>
              <div data-testid="lead-detail-campaign" className="mt-1">{campaignTitle}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 font-medium">Created On</div>
              <div data-testid="lead-detail-created-at" className="mt-1">{new Date(lead.created_at).toLocaleString()}</div>
            </div>
          </div>
          
          {lead.personalisation && (
            <div>
              <div className="text-sm text-gray-600 font-medium mb-2">Personalisation Message</div>
              <div data-testid="lead-detail-personalisation" className="border rounded p-3 bg-gray-50 max-h-64 overflow-y-auto whitespace-pre-wrap">
                {lead.personalisation}
              </div>
            </div>
          )}
          
          {lead.org_id && (
            <div>
              <div className="text-sm text-gray-600 font-medium">Organisation ID</div>
              <div data-testid="lead-detail-org-id" className="mt-1 text-sm text-gray-500 break-all">{lead.org_id}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonalisationCell({ text }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  
  const handleMouseEnter = (e) => {
    if (!text || text === '-') return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.bottom + 5 });
    setShowTooltip(true);
  };
  
  return (
    <>
      <div 
        className="max-w-xs truncate cursor-help relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {text || '-'}
      </div>
      
      {showTooltip && text && text !== '-' && (
        <div 
          className="fixed bg-white border border-gray-300 rounded shadow-lg p-3 z-50 max-w-md max-h-96 overflow-y-auto"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="text-sm whitespace-pre-wrap break-words">
            {text}
          </div>
        </div>
      )}
    </>
  );
}

export default function Collections(){
  const [rows,setRows]=useState([]);
  const [cid,setCid]=useState("");
  const [startDate,setStartDate]=useState("");
  const [endDate,setEndDate]=useState("");
  const [campaigns,setCampaigns]=useState([]);
  const [campaignsMap,setCampaignsMap]=useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  const load = async ()=>{
    try {
      // Always use /collections - it filters by org_id (including impersonated org)
      const endpoint = '/collections';
      const params = { page, per_page: perPage };
      if (cid) params.campaign_id = cid;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      // Load collections (which now includes campaign_title in each row)
      const r = await api.get(endpoint, { params });
      
      // Load campaigns for the filter dropdown
      const c = await api.get('/campaigns').catch((err)=>{
        console.error('Failed to load campaigns:', err);
        return { data: [] };
      });
      
      const clist = Array.isArray(c.data) ? c.data : (c.data?.rows || []);
      setCampaigns(clist);
      setRows(r.data.rows||[]);
      setTotalCount(r.data.total || r.data.rows?.length || 0);
      setSelectedIds(new Set()); // Clear selection on reload
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load leads');
    }
  };
  
  useEffect(()=>{ load(); },[page, perPage]);
  
  const totalPages = Math.ceil(totalCount / perPage);
  
  const fmt = (iso)=>{ try{ return new Date(iso).toLocaleString(); }catch{return iso;} };
  // Use campaign_title from the row data directly
  const title = (row)=> row.campaign_title || row.campaign_id || 'Unknown Campaign';
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map(r => r.id)));
    }
  };
  
  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} lead(s)? This cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      // Always use /collections/delete - it validates org ownership
      const endpoint = '/collections/delete';
      const result = await api.post(endpoint, { lead_ids: Array.from(selectedIds) });
      toast.success(`Deleted ${result.data.deleted} lead(s)`);
      setSelectedIds(new Set());
      await load();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete leads');
    } finally {
      setDeleting(false);
    }
  };
  
  const exportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Please log in to export data.");
        return;
      }
      
      // Always use /collections/export - it filters by org_id
      const endpoint = '/collections/export';
      const params = new URLSearchParams();
      if (cid) params.append('campaign_id', cid);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${baseUrl}/api${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Try opening in new window first (works better in preview environments)
      const newWindow = window.open(downloadUrl, '_blank');
      if (!newWindow) {
        // Fallback to link click method
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="collections-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>Leads</h1>
          <p className="text-sm text-gray-500">View and export campaign submissions.</p>
        </div>
        <Button data-testid="leads-back" variant="outline" onClick={()=>window.location.href='/campaigns'}>Back to Campaigns</Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between w-full">
            <span>Filter Leads</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="col-refresh">Refresh</Button>
              <Button size="sm" onClick={exportCSV} data-testid="col-export">Export CSV</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Campaign</label>
              <select 
                data-testid="col-campaign" 
                className="border rounded px-3 py-2 w-full" 
                value={cid} 
                onChange={(e)=>{setCid(e.target.value); }}
              >
                <option value="">All Campaigns</option>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Start Date</label>
              <Input 
                type="date" 
                data-testid="col-start-date" 
                value={startDate} 
                onChange={(e)=>setStartDate(e.target.value)} 
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">End Date</label>
              <Input 
                type="date" 
                data-testid="col-end-date" 
                value={endDate} 
                onChange={(e)=>setEndDate(e.target.value)} 
              />
            </div>
            
            <div className="flex items-end">
              <Button className="w-full" onClick={() => { setPage(1); load(); }} data-testid="col-apply">Apply Filters</Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between w-full">
            <span>Leads ({rows.length})</span>
            {selectedIds.size > 0 && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={deleteSelected}
                disabled={deleting}
                data-testid="delete-selected"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input 
                      type="checkbox" 
                      checked={rows.length > 0 && selectedIds.size === rows.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4"
                      data-testid="select-all"
                    />
                  </TableHead>
                  {["Name","Email","Campaign","Created On","Personalisation","Actions"].map(h=> (<TableHead key={h}>{h}</TableHead>))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      No leads found. Try adjusting your filters or generate some leads via your campaigns.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r,i)=> (
                    <TableRow key={r.id||i} className={`hover:bg-gray-50 ${selectedIds.has(r.id) ? 'bg-blue-50' : ''}`}>
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="w-4 h-4"
                          data-testid={`select-${r.id}`}
                        />
                      </TableCell>
                      <TableCell>{r.user_name||'-'}</TableCell>
                      <TableCell>{r.user_email}</TableCell>
                      <TableCell>{title(r)}</TableCell>
                      <TableCell>{fmt(r.created_at)}</TableCell>
                      <TableCell>
                        <PersonalisationCell text={r.personalisation} />
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setSelectedLead(r)}
                          data-testid={`view-lead-${r.id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select 
                className="border rounded px-2 py-1" 
                value={perPage} 
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>per page</span>
              <span className="ml-4">Total: {totalCount} leads</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Previous
              </Button>
              <span className="text-sm px-3">
                Page {page} of {totalPages || 1}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {selectedLead && (
        <LeadDetailsModal 
          lead={selectedLead} 
          campaignTitle={title(selectedLead)}
          onClose={() => setSelectedLead(null)} 
        />
      )}
    </div>
  );
}
